/**
 * Filter Analytics
 *
 * Centralised GA4 / GTM dataLayer instrumentation for every filter bar on the
 * site (general show filters, ticket booth, today's tickets, campaign, access
 * and news). Each filter system creates one tracker via createFilterTracker()
 * and feeds it the current filter params on every apply / clear / render. The
 * tracker diffs the new params against the previous snapshot and pushes one
 * standardised `filter_interaction` event per changed filter value.
 *
 * Event schema (single event name, action carried as a parameter):
 *
 *   {
 *     event: 'filter_interaction',
 *     filter_context: 'ticket_booth',   // which filter system (snake_case)
 *     filter_name:    'genre',          // canonical filter key (snake_case)
 *     filter_action:  'apply',          // 'apply' | 'remove' | 'update'
 *     filter_value:   'musicals',       // the value applied or removed
 *     filter_previous_value: '20'       // present on 'update' only
 *   }
 *
 * Multi-select filters (genre, time, shows, access_type) emit one event per
 * individual value added or removed. Scalar filters (search, date, price,
 * sort, age_guide and the boolean chips) emit a single event describing the
 * transition (apply / remove / update).
 *
 * The first snapshot per tracker — established via init(), or lazily on the
 * first track() call — is treated as the page-load baseline and never emits.
 * Only subsequent user-driven changes produce events. Because the differ only
 * emits when the canonical state actually changes, it is safe (and idempotent)
 * to call track() from every render, including the duplicate renders that some
 * filter bars fire from overlapping Apply handlers.
 */

const EVENT_NAME = 'filter_interaction';

const str = (v) => ((v === undefined || v === null) ? '' : String(v).trim());

const toArray = (v) => {
	if (Array.isArray(v)) {
		return v.map(str).filter(Boolean);
	}
	const s = str(v);
	return s ? s.split(',').map((x) => x.trim()).filter(Boolean) : [];
};

const SORT_ALIASES = {
	'name-atz': 'name_asc', 'name-zta': 'name_desc',
	'date-old': 'date_old', 'date-new': 'date_new',
	'price-low': 'price_low', 'price-high': 'price_high'
};

// Normalise a sort value and collapse the default (A–Z) to empty so the
// implicit default ordering is never reported as an active filter.
const normaliseSortValue = (v) => {
	const s = SORT_ALIASES[ str(v) ] || str(v);
	return s === 'name_asc' ? '' : s;
};

// Compose a single human-readable value for a date range filter.
const dateRangeValue = (from, to) => {
	const f = str(from);
	const t = str(to);
	if (!f && !t) {
		return '';
	}
	if (f && t) {
		return f === t ? f : `${ f }..${ t }`;
	}
	return f || t;
};

// Boolean toggle chips are reported as 'true' when on and '' (absent) when off,
// so the differ turns them into apply / remove events.
const boolValue = (v) => (str(v) === '1' ? 'true' : '');

// Boolean toggle chips shared across the show / ticket filter family.
const BOOL_FLAGS = ['today', 'tomorrow', 'access', 'offers', 'coming_soon', 'last_chance', 'advance_offers'];

/**
 * Schema for the show / ticket filter family (general filters, ticket booth,
 * today's tickets, campaign and access). Absent keys resolve to empty, so this
 * single schema safely covers modules that only use a subset of these filters.
 */
export const SHOW_FILTER_SCHEMA = {
	multi: [
		{ name: 'genre', key: 'genre' },
		{ name: 'time', key: 'time' },
		{ name: 'shows', key: 'shows' },
		{ name: 'access_type', key: 'type' }
	],
	scalars: [
		{ name: 'search', get: (p) => str(p.q) },
		{ name: 'price', get: (p) => ((str(p.max) !== '' && Number(p.max) < 100) ? str(p.max) : '') },
		{ name: 'sort', get: (p) => normaliseSortValue(p.sort) },
		{ name: 'age_guide', get: (p) => str(p.age_guide) },
		{ name: 'date', get: (p) => dateRangeValue(p.from, p.to) },
		...BOOL_FLAGS.map((k) => ({ name: k, get: (p) => boolValue(p[ k ]) }))
	]
};

/**
 * Schema for the news listing filters. `type` and `category` are page context
 * (fixed per listing) but are included for completeness so a future filterable
 * news view is covered automatically.
 */
export const NEWS_FILTER_SCHEMA = {
	multi: [],
	scalars: [
		{ name: 'search', get: (p) => str(p.q) },
		{ name: 'news_label', get: (p) => str(p.label) },
		{ name: 'news_type', get: (p) => str(p.type) },
		{ name: 'news_category', get: (p) => str(p.category) }
	]
};

// Reduce a raw params object to a normalised snapshot the differ can compare.
const canonicalize = (params, schema) => {
	const source = params || {};
	const scalar = {};
	schema.scalars.forEach(({ name, get }) => {
		scalar[ name ] = get(source) || '';
	});
	const multi = {};
	schema.multi.forEach(({ name, key }) => {
		multi[ name ] = toArray(source[ key ]);
	});
	return { scalar, multi };
};

const pushEvent = (payload) => {
	if (typeof window === 'undefined') {
		return;
	}
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push(payload);
};

const emit = (context, name, action, value, previousValue) => {
	const payload = {
		event: EVENT_NAME,
		filter_context: context,
		filter_name: name,
		filter_action: action,
		filter_value: str(value)
	};
	if (action === 'update') {
		payload.filter_previous_value = str(previousValue);
	}
	pushEvent(payload);
};

/**
 * Create a filter tracker bound to a single filter system.
 *
 * @param {string} context  - Canonical filter system name (e.g. 'ticket_booth').
 * @param {Object} [schema] - Filter schema describing which scalar and
 *                          multi-select filters to diff.
 * @return {{init: Function, track: Function}} The tracker instance.
 */
export function createFilterTracker(context, schema = SHOW_FILTER_SCHEMA) {
	let prev = null;

	const setBaseline = (params) => {
		prev = canonicalize(params, schema);
	};

	return {
		/**
		 * Establish the page-load baseline without emitting any events. Call
		 * once after the form has been seeded from the URL (and any show-search
		 * tags mounted), so pre-selected filters are not reported as new applies.
		 *
		 * @param {Object} params - The initial filter params.
		 */
		init(params) {
			setBaseline(params);
		},

		/**
		 * Diff the given params against the previous snapshot and emit one
		 * `filter_interaction` event per changed value. Safe to call on every
		 * render — identical params produce no events.
		 *
		 * @param {Object} params - The current filter params.
		 */
		track(params) {
			if (!params || typeof params !== 'object') {
				return;
			}

			if (prev === null) {
				// No baseline yet: treat this call as the baseline so a missing
				// init() never turns the first user action into a phantom event.
				setBaseline(params);
				return;
			}

			const next = canonicalize(params, schema);

			// Scalar filters: a single transition event per filter.
			schema.scalars.forEach(({ name }) => {
				const before = prev.scalar[ name ] || '';
				const after = next.scalar[ name ] || '';
				if (before === after) {
					return;
				}
				if (!before && after) {
					emit(context, name, 'apply', after);
				} else if (before && !after) {
					emit(context, name, 'remove', before);
				} else {
					emit(context, name, 'update', after, before);
				}
			});

			// Multi-select filters: one event per value added or removed.
			schema.multi.forEach(({ name }) => {
				const before = new Set(prev.multi[ name ] || []);
				const after = new Set(next.multi[ name ] || []);
				after.forEach((v) => {
					if (!before.has(v)) {
						emit(context, name, 'apply', v);
					}
				});
				before.forEach((v) => {
					if (!after.has(v)) {
						emit(context, name, 'remove', v);
					}
				});
			});

			prev = next;
		}
	};
}
